/**
 * Expected-term validation for the Syncs table.
 *
 * The feed comes over in two schedule slots: the PRIMARY extract every hour,
 * and a SECONDARY extract on the 2 AM (Pacific) run. The Canvas admin enters
 * the expected 4-digit term for each slot on the Settings page; here we
 * compare each sync's worker-detected term(s) against the expectation for
 * its slot and flag mismatches. DISPLAY ONLY — nothing is pushed anywhere;
 * feed-term changes require a ticket.
 */

import type { TriggerType } from "@canvas-sync/shared";

export interface ExpectedTerms {
  primary?: string;
  secondary?: string;
}

export type TermSlot = "primary" | "secondary";

/** A sync queued between 2:00–2:59 AM Pacific belongs to the secondary feed. */
export function runTermSlot(queuedAt: string): TermSlot {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      hour: "numeric",
      hourCycle: "h23",
    }).format(new Date(queuedAt)),
  );
  return hour === 2 ? "secondary" : "primary";
}

export interface TermCheck {
  slot: TermSlot;
  /** The term(s) the slot expects this sync's file to carry. */
  expected: string[];
  /** True when expectations exist, terms were detected, and they don't fit. */
  mismatch: boolean;
}

/**
 * Scheduled syncs are held to their slot's expected term. Manual syncs and
 * reruns can legitimately replay either feed's file at any hour, so they
 * only flag when their term matches NEITHER expected term — otherwise a
 * 10 AM rerun of the 2 AM file would be permanent false-positive amber.
 * A multi-term file always flags (each feed extract is single-term).
 */
export function checkRunTerm(
  run: { queuedAt: string; trigger: TriggerType; detectedTerms?: string[] },
  expectedTerms: ExpectedTerms,
): TermCheck {
  const slot = runTermSlot(run.queuedAt);
  const scheduled = run.trigger === "n8n" || run.trigger === "scheduled";
  const expected = (
    scheduled
      ? [expectedTerms[slot]]
      : [expectedTerms.primary, expectedTerms.secondary]
  ).filter((t): t is string => Boolean(t));

  const detected = run.detectedTerms ?? [];
  const mismatch =
    expected.length > 0 &&
    detected.length > 0 &&
    !(detected.length === 1 && detected[0] && expected.includes(detected[0]));
  return { slot, expected, mismatch };
}
